import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { ApplicationState, AppState } from '../../../store';
import { withSnackbar, ProviderContext } from 'notistack';
import {
  OrganizationOwnMake,
  SaveOrganizationOwnMakesRequestDto
} from '../../../interfaces/Organization';
import { ITranslatorService } from '../../../services/Interfaces/ITranslatorService';
import { ApplicationContext, AppContext } from '../../../context/Contexts';
import { IOrganizationService } from '../../../services/Interfaces/IOrganizationService';
import { IReferentialService } from '../../../services/Interfaces/IReferentialService';
import { Box, Button, Card, CardContent, Tabs, Tab, IconButton } from '@material-ui/core';
import { ScaleLoader } from 'react-spinners';
import MUIDataTable, {
  MUIDataTableColumnOptions,
  MUIDataTableMeta,
  SelectableRows
} from 'mui-datatables';
import moment from 'moment';
import { MUITranslations } from '../../../helpers/MUITableTranslations';
import { RouteComponentProps } from 'react-router';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { IReferential } from '../../../interfaces/IReferential';
import { ReferentialCode } from '../../../helpers/Constants';
import Referentials from '../../../helpers/Referentials.json';
import DeleteOutlineTwoToneIcon from '@material-ui/icons/DeleteOutlineTwoTone';

interface IOrganizationOwnMakesState {
  organizationOwnMakes: OrganizationOwnMake[];
  isLoading: boolean;
  brands: IReferential[];
  selectedBrands: IReferential[];
  organizationId: number;
}

type IOrganizationOwnMakesProps = { appState: AppState } & ProviderContext &
  RouteComponentProps<{ id: string }>;

class OrganizationOwnMakes extends React.PureComponent<
  IOrganizationOwnMakesProps,
  IOrganizationOwnMakesState
> {
  private translatorService!: ITranslatorService;
  private organizationService!: IOrganizationService;
  private referentialService!: IReferentialService;

  static contextType = ApplicationContext;

  state = {
    organizationOwnMakes: [],
    isLoading: false,
    brands: [],
    selectedBrands: [],
    organizationId: 0
  } as IOrganizationOwnMakesState;

  public componentDidMount() {
    this.setState({ isLoading: true }, async () => {
      await this.loadOrganizationOwnMakes();
    });
  }

  loadOrganizationOwnMakes = async () => {
    const organizationId = Number.parseInt(this.props.match.params.id);

    if (Number.isNaN(organizationId)) {
      return;
    }

    const brandRef = Referentials.referential.find(
      (item) => item.code === ReferentialCode.CarBrands
    );

    let organizationOwnMakes: OrganizationOwnMake[];
    let brands: IReferential[];
    try {
      [organizationOwnMakes, brands] = await Promise.all([
        this.organizationService.GetOrganizationOwnMakes(organizationId),
        this.referentialService.Get(brandRef!.baseUrl)
      ]);
    } catch (error) {
      const message =
        (error as any)?.response?.status === 403
          ? this.translatorService.Tranlate(
              'ORGANIZATION_OWN_MAKES_FORBIDDEN_MSG',
              'Nu aveti acces la marcile proprii ale acestei organizatii!'
            )
          : this.translatorService.Tranlate('ERROR_MSG', 'Eroare');
      this.props.enqueueSnackbar(message, { variant: 'error' });
      this.setState({ organizationOwnMakes: [], isLoading: false });
      return;
    }

    organizationOwnMakes.forEach((ownMake) => {
      ownMake.brand = brands.find((item) => item.id === ownMake.brandId) ?? null;
    });

    this.setState({
      organizationOwnMakes: organizationOwnMakes,
      brands: brands.filter((item) => item.isActive === true),
      isLoading: false,
      organizationId: organizationId
    });
  };

  getColumns = () => {
    const columns = [
      {
        name: 'id',
        options: { display: 'excluded', filter: false } as MUIDataTableColumnOptions
      },
      {
        name: 'brand',
        label: this.translatorService.Tranlate('ORGANIZATIONS_TAB_OWN_MAKE', 'Marca'),
        options: {
          filter: false,
          sort: false,
          customBodyRender: (value: IReferential | null) => {
            return value ? value.displayName : '';
          }
        } as MUIDataTableColumnOptions
      },
      {
        name: 'dateModification',
        label: this.translatorService.Tranlate(
          'WORKFLOW_PARTNERS_TAB_DATE_MODIFICATION_HEADER',
          'Data Modificare'
        ),
        options: {
          filter: false,
          sort: false,
          customBodyRender: (value: Date) =>
            moment.utc(value).local().format(this.props.appState.longDateFormat)
        } as MUIDataTableColumnOptions
      },
      {
        name: '',
        options: {
          setCellHeaderProps: () => ({ align: 'center' }),
          setCellProps: () => ({ align: 'center' }),
          customBodyRender: (value: boolean, tableMeta: MUIDataTableMeta) => {
            return (
              <IconButton
                aria-label="delete"
                color="inherit"
                className="text-danger"
                size="small"
                onClick={() => this.deleteOrganizationOwnMake(tableMeta)}
              >
                <DeleteOutlineTwoToneIcon />
              </IconButton>
            );
          }
        }
      }
    ];

    return columns;
  };

  deleteOrganizationOwnMake = async (tableMeta: MUIDataTableMeta) => {
    try {
      const id = tableMeta.rowData[0];

      await this.organizationService.RemoveOrganizationOwnMake(id);

      this.setState({ isLoading: true }, () => {
        this.loadOrganizationOwnMakes();
      });

      this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
        variant: 'success'
      });
    } catch (error) {
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    }
  };

  addOrganizationOwnMakes = async () => {
    try {
      const duplicateBrandIds = this.state.selectedBrands
        .filter((brand) =>
          this.state.organizationOwnMakes.some((ownMake) => ownMake.brandId === brand.id)
        )
        .map((brand) => brand.id);
      if (duplicateBrandIds.length > 0) {
        this.showOwnMakeExistsError(duplicateBrandIds);
        return;
      }

      const response = await this.organizationService.SaveOrganizationOwnMakes({
        organizationId: this.state.organizationId,
        brandIds: this.state.selectedBrands.map((brand) => brand.id)
      } as SaveOrganizationOwnMakesRequestDto);
      if (!response.success) {
        this.showOwnMakeExistsError(response.duplicateBrandIds);
        return;
      }

      this.setState({ isLoading: true, selectedBrands: [] }, () => {
        this.loadOrganizationOwnMakes();
      });

      this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
        variant: 'success'
      });
    } catch (error) {
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    }
  };

  showOwnMakeExistsError = (brandIds: number[]) => {
    const brandNames = this.state.brands
      .filter((brand) => brandIds.includes(brand.id))
      .map((brand) => brand.displayName)
      .join(', ');
    const message = this.translatorService.Tranlate(
      'ORGANIZATION_OWN_MAKES_EXISTS_MSG',
      'Marca exista deja!'
    );
    this.props.enqueueSnackbar(`${message} ${brandNames}`, { variant: 'error' });
  };

  handleBrandsChange = (newValue: IReferential[]) => {
    this.setState({ selectedBrands: newValue });
  };

  renderAddForm() {
    return (
      <Box mt={1} pt={1}>
        <Card elevation={1}>
          <CardContent>
            <ValidatorForm onSubmit={this.addOrganizationOwnMakes}>
              <Autocomplete
                id="ownMakes"
                className="m-2"
                fullWidth
                multiple
                options={[...this.state.brands].sort((a, b) =>
                  a.displayName.localeCompare(b.displayName)
                )}
                value={this.state.selectedBrands}
                onChange={(e: any, newValue: IReferential[]) => this.handleBrandsChange(newValue)}
                getOptionLabel={(option: IReferential) => option.displayName || ''}
                renderInput={(params) => (
                  <TextValidator
                    {...params}
                    name="ownMake"
                    value={this.state.selectedBrands}
                    label={this.translatorService.Tranlate('ORGANIZATIONS_TAB_OWN_MAKE', 'Marca')}
                    fullWidth
                    validators={['required']}
                    errorMessages={[
                      this.translatorService.Tranlate(
                        'VALIDATORS_REQUIRED',
                        'Campul este obligatoriu'
                      )
                    ]}
                  />
                )}
              />

              <div className="text-right">
                <Button className="m-2" variant="contained" color="secondary" type="submit">
                  {this.translatorService.Tranlate('ADMIN_EDIT_USER_ROLES_BTN_ADD', 'ADAUGA')}
                </Button>
              </div>
            </ValidatorForm>
          </CardContent>
        </Card>
      </Box>
    );
  }

  public render() {
    this.translatorService = (this.context as AppContext).translatorService;
    this.organizationService = (this.context as AppContext).organizationService;
    this.referentialService = (this.context as AppContext).referentialService;

    const options = {
      viewColumns: false,
      selectableRows: 'none' as SelectableRows,
      selectableRowsOnClick: false,
      print: false,
      download: false,
      textLabels: MUITranslations.GetTranslations(this.translatorService),
      elevation: 1
    };

    return (
      <Fragment>
        <Box>
          <Card>
            <CardContent>
              <Tabs
                value={0}
                indicatorColor="primary"
                textColor="primary"
                variant="standard"
                color="primary"
              >
                <Tab
                  style={{ textTransform: 'none' }}
                  label={this.translatorService.Tranlate(
                    'ORGANIZATIONS_TAB_OWN_MAKES',
                    'Marci proprii'
                  )}
                />
              </Tabs>
            </CardContent>
          </Card>
        </Box>
        {this.renderAddForm()}
        <Box mt={1} pt={1}>
          <div className="d-flex flex-row text-center flex-wrap justify-content-center">
            <ScaleLoader color={'var(--primary)'} loading={this.state.isLoading} />
          </div>
          {!this.state.isLoading ? (
            <MUIDataTable
              title={''}
              data={this.state.organizationOwnMakes}
              columns={this.getColumns()}
              options={options}
            />
          ) : null}
        </Box>
      </Fragment>
    );
  }
}

export default connect(
  (state: ApplicationState) => ({
    appState: state.app
  }),
  null
)(withSnackbar(OrganizationOwnMakes as any));
